import type { TFunction } from "i18next";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { Colleague } from "../../Connections/types/Colleague.type";
import {
  isConferenceVisitorRegistrationRow,
  isVisitorScheduleFieldEditable,
  type VisitorScheduleField
} from "../../../components/Schedule/functions/visitor-schedule-field-editable.function";

/** Row shape for MRT + Fleet date editors (`start`/`end` ISO, `isCheckedIn` mirrors `checkedIn`). */
export type VisitorBookingTableRow = BookingScheduleInterface & {
  start: string;
  end: string;
  isCheckedIn?: boolean;
  rowId: string;
};

export type VisitorBookingEditDraft = {
  externalName?: string;
  externalCompany?: string;
  externalEmail?: string;
  externalContacts?: string;
  externalVisitReason?: string;
  userBookedBy?: string;
  locationName?: string;
  start?: string;
  end?: string;
};

export type VisitorAdminScheduleField = VisitorScheduleField;

export { isConferenceVisitorRegistrationRow };

export function buildVisitorBookingTableRowId(row: BookingScheduleInterface): string {
  if (isConferenceVisitorRegistrationRow(row) && row.visitorRegistrationId != null) {
    return `visitor-reg-${row.visitorRegistrationId}`;
  }
  return `visitor-booking-${row.id}`;
}

export function isVisitorAdminTableRowEditable(visit: BookingScheduleInterface): boolean {
  void visit;
  return true;
}

export function isVisitorAdminScheduleFieldEditable(
  visit: BookingScheduleInterface,
  field: VisitorAdminScheduleField
): boolean {
  if (!isVisitorAdminTableRowEditable(visit)) {
    return false;
  }

  return isVisitorScheduleFieldEditable(visit, field);
}

/** One MRT row per schedule item — BE returns flat rows (no client-side merge). */
export function mapRowsForTable(rows: BookingScheduleInterface[]): VisitorBookingTableRow[] {
  return rows.map(row => {
    const start = row.start ?? row.startDate;
    const end = row.end ?? row.endDate;

    return {
      ...row,
      start: start ?? "",
      end: end ?? "",
      isCheckedIn: row.checkedIn,
      rowId: buildVisitorBookingTableRowId(row)
    };
  });
}

export function visitorBookingHostDisplay(
  row: BookingScheduleInterface,
  colleagues: Colleague[]
): string {
  const uid = row.userBookedBy?.trim() || row.userBookedFor?.trim();
  if (!uid) {
    return "—";
  }
  const c = colleagues.find(x => x.userId === uid);
  if (c) {
    const name = `${c.firstName} ${c.surname}`.trim();
    return name || c.email || uid;
  }
  return uid;
}

export function visitorBookingLocationDisplay(row: BookingScheduleInterface): string {
  const locationName = row.locationName?.trim();
  return locationName || "—";
}

export function visitorBookingVisitorCount(row: BookingScheduleInterface): number {
  const visitorBookingsCount = row.visitorBookings?.length ?? 0;
  if (visitorBookingsCount > 0) {
    return visitorBookingsCount;
  }

  const guestVisitorsCount = row.guests?.filter(guest => guest.external).length ?? 0;
  if (guestVisitorsCount > 0) {
    return guestVisitorsCount;
  }

  const hasExternalFields = !!(row.externalName?.trim() || row.externalEmail?.trim());
  return hasExternalFields ? 1 : 0;
}

function consentSignatureStorageFilterKey(signatureStored: boolean | undefined): string {
  if (signatureStored === true) {
    return "sig1";
  }
  if (signatureStored === false) {
    return "sig0";
  }
  return "sig?";
}

/**
 * Stable string for the visitor bookings consent column (MRT sort/filter);
 * uses translated placeholder when the schedule row has no consent documents.
 */
export function visitorBookingConsentColumnAccessorValue(
  row: Pick<BookingScheduleInterface, "visitConsent">,
  t: TFunction
): string {
  const documents = row.visitConsent?.documents;
  if (!documents?.length) {
    return t("_visitorBookings_consentNotInSchedule");
  }
  const parts = documents.map(d => `${d.title?.trim() || d.documentId}:${d.consented ? "1" : "0"}`);
  return `${parts.join("|")}|${consentSignatureStorageFilterKey(row.visitConsent?.signatureStored)}`;
}
